// src/components/AssistanceLevelSelector.jsx
import React, { useState } from "react";
import { FiAlertTriangle, FiZap, FiCheckSquare, FiTool, FiUsers } from "react-icons/fi";
import "../styles/AssistanceLevelSelector.css";

export const LEVEL_DATA = {
  1: { name: "Hint Only", description: "Concepts and hints only", color: "#f59e0b", icon: <FiZap />, tooltip: "Nudges you in the right direction with conceptual hints — no direct answers." },
  2: { name: "Review", description: "Review your work, feedback only", color: "#10b981", icon: <FiCheckSquare />, tooltip: "Submit your work and get targeted feedback on what's right and what to revisit." },
  3: { name: "Troubleshoot", description: "Help find and fix errors", color: "#06b6d4", icon: <FiTool />, tooltip: "Paste your code or work and get help identifying and resolving specific errors." },
  4: { name: "Co-Builder", description: "Work through it together", color: "#3b82f6", icon: <FiUsers />, tooltip: "Collaborate step-by-step — you drive, the AI helps you build the solution together." },
  5: { name: "Full Solutions", description: "Complete solutions (learning only)", color: "#ef4444", warning: true, icon: <FiAlertTriangle />, tooltip: "Provides complete solutions for learning purposes only — not for submission." },
};

const AssistanceLevelSelector = ({
  selectedLevel,
  onLevelChange,
  loading,
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [pendingLevel, setPendingLevel] = useState(null);
  const [hoveredLevel, setHoveredLevel] = useState(null);

  const handleLevelClick = (level) => {
    if (loading) return;
    
    // Show warning for Level 5
    if (level === 5 && selectedLevel !== 5) {
      setShowWarning(true);
      setPendingLevel(5);
      return;
    }
    
    onLevelChange(level);
  };

  const confirmLevel6 = () => {
    onLevelChange(5);
    setShowWarning(false);
    setPendingLevel(null);
  };

  const cancelLevel6 = () => {
    setShowWarning(false);
    setPendingLevel(null);
  };

  return (
    <div className="assistance-level-container">
      <div className="level-options">
        {Object.entries(LEVEL_DATA).map(([level, data]) => {
          const levelNum = parseInt(level, 10);
          const isActive = selectedLevel === levelNum;
          return (
            <button
              key={level}
              className={`level-option ${isActive ? "active" : ""} ${data.warning ? "warning-level" : ""}`}
              onClick={() => handleLevelClick(levelNum)}
              onMouseEnter={() => setHoveredLevel(levelNum)}
              onMouseLeave={() => setHoveredLevel(null)}
              disabled={loading}
              style={{ "--level-color": data.color }}
            >
              <span className="level-icon">{data.icon}</span>
              <span className="level-name">{data.name}</span>
              {hoveredLevel === levelNum && (
                <span className="level-tooltip" style={{ "--tooltip-color": data.color }}>
                  {data.tooltip}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Level 5 Warning Modal */}
      {showWarning && (
        <div className="level-warning-overlay" onClick={cancelLevel6}>
          <div className="level-warning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="warning-icon">
              <FiAlertTriangle size={32} />
            </div>
            <h3>⚠️ Full Solutions Mode</h3>
            <p>
              <strong>This level is NOT ALLOWED for course submissions.</strong>
            </p>
            <p>
              Content generated at this level must be treated as <strong>reference material only</strong> —
              study it to learn, but you may NOT submit AI-generated solutions.
            </p>
            <p>
              Consider using <strong>Level 4 (Co-Builder)</strong> where you work through it together and make final decisions.
            </p>
            <div className="warning-actions">
              <button className="cancel-btn" onClick={cancelLevel6}>
                Go Back
              </button>
              <button className="confirm-btn" onClick={confirmLevel6}>
                I Understand, Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssistanceLevelSelector;
